/**
 * Derive Floor Sheet weld completion times from Data360 / telemetry samples.
 *
 * Mapping rules (Dan Moers PQR feedback — prefer logs over manual entry):
 * - rootComplete  = max(recordedAt) among Root-family pass_mapped labels
 * - hotPassStart  = min(recordedAt) among Hot-family pass_mapped labels
 * - weldCompleted = max(recordedAt) across all samples with a timestamp
 *
 * Timestamps are stored as UTC ISO on samples (`recordedAt` from Data360
 * `s_time`). Slot date/time uses the UTC calendar components so PQR print
 * matches the log wall-clock without a local TZ shift.
 */

import {
  emptyFsTimes,
  type FloorSheetFsTimeSlot,
  type FloorSheetFsTimes
} from './floorSheetFsTimes'

export type DeriveFsTimesSample = {
  passMapped: string | null | undefined
  recordedAt: string | null | undefined
}

export type FloorSheetFsTimesLogSnapshot = {
  derivedAt: string
  rootCompleteAt: string | null
  hotPassStartAt: string | null
  weldCompletedAt: string | null
  counts: { root: number; hot: number; all: number }
  timezone: 'UTC'
}

export function normalizeFsTimesLogSnapshot(raw: unknown): FloorSheetFsTimesLogSnapshot | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const counts =
    o.counts && typeof o.counts === 'object' && !Array.isArray(o.counts)
      ? (o.counts as Record<string, unknown>)
      : {}
  return {
    derivedAt: typeof o.derivedAt === 'string' ? o.derivedAt : '',
    rootCompleteAt: typeof o.rootCompleteAt === 'string' ? o.rootCompleteAt : null,
    hotPassStartAt: typeof o.hotPassStartAt === 'string' ? o.hotPassStartAt : null,
    weldCompletedAt: typeof o.weldCompletedAt === 'string' ? o.weldCompletedAt : null,
    counts: {
      root: Number(counts.root) || 0,
      hot: Number(counts.hot) || 0,
      all: Number(counts.all) || 0
    },
    timezone: 'UTC'
  }
}

export type DeriveFloorSheetFsTimesResult = {
  fsTimes: FloorSheetFsTimes
  snapshot: FloorSheetFsTimesLogSnapshot
  /** True when at least one slot received a date/time from logs. */
  hasAny: boolean
}

/** Data360 zone codes commonly used for root before pass rename. */
const ROOT_RAW_ZONE_CODES = new Set(['rt', 'rm', 'ro', 'rb'])

/** Data360 zone codes commonly used for hot before pass rename. */
const HOT_RAW_ZONE_CODES = new Set(['ho', 'hb', 'ht'])

/**
 * Root-family labels: "Root", "Root RC", "Root Misfire…", and unmapped
 * Data360 zone codes RT / RM / RO / RB.
 */
export function isRootFamilyPassLabel(label: string): boolean {
  const t = label.trim().toLowerCase()
  if (!t) return false
  if (t === 'root' || t.startsWith('root ')) return true
  return ROOT_RAW_ZONE_CODES.has(t)
}

/** Hot-family labels: "Hot…", and unmapped Data360 codes HO / HB / HT. */
export function isHotFamilyPassLabel(label: string): boolean {
  const t = label.trim().toLowerCase()
  if (!t) return false
  if (t === 'hot' || t.startsWith('hot ')) return true
  return HOT_RAW_ZONE_CODES.has(t)
}

function isoToSlot(iso: string): FloorSheetFsTimeSlot {
  const m = iso.trim().match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/)
  if (!m) return { date: '', time: '' }
  return { date: m[1], time: `${m[2]}:${m[3]}` }
}

function maxIso(values: string[]): string | null {
  if (values.length === 0) return null
  return values.reduce((a, b) => (a >= b ? a : b))
}

function minIso(values: string[]): string | null {
  if (values.length === 0) return null
  return values.reduce((a, b) => (a <= b ? a : b))
}

export function deriveFloorSheetFsTimesFromSamples(
  samples: DeriveFsTimesSample[]
): DeriveFloorSheetFsTimesResult {
  const rootTimes: string[] = []
  const hotTimes: string[] = []
  const allTimes: string[] = []

  for (const sample of samples) {
    const iso = typeof sample.recordedAt === 'string' ? sample.recordedAt.trim() : ''
    if (!iso || Number.isNaN(Date.parse(iso))) continue
    allTimes.push(iso)
    const pass = typeof sample.passMapped === 'string' ? sample.passMapped : ''
    if (isRootFamilyPassLabel(pass)) rootTimes.push(iso)
    if (isHotFamilyPassLabel(pass)) hotTimes.push(iso)
  }

  const rootCompleteAt = maxIso(rootTimes)
  const hotPassStartAt = minIso(hotTimes)
  const weldCompletedAt = maxIso(allTimes)

  const fsTimes = emptyFsTimes()
  if (rootCompleteAt) fsTimes.rootComplete = isoToSlot(rootCompleteAt)
  if (hotPassStartAt) fsTimes.hotPassStart = isoToSlot(hotPassStartAt)
  if (weldCompletedAt) fsTimes.weldCompleted = isoToSlot(weldCompletedAt)

  return {
    fsTimes,
    snapshot: {
      derivedAt: new Date().toISOString(),
      rootCompleteAt,
      hotPassStartAt,
      weldCompletedAt,
      counts: { root: rootTimes.length, hot: hotTimes.length, all: allTimes.length },
      timezone: 'UTC'
    },
    hasAny: Boolean(rootCompleteAt || hotPassStartAt || weldCompletedAt)
  }
}

function slotKey(slot: FloorSheetFsTimeSlot): string {
  return `${slot.date.trim()}T${slot.time.trim()}`
}

/** True when any filled slot differs from the log snapshot (manual edit). */
export function fsTimesDeviateFromSnapshot(
  fsTimes: FloorSheetFsTimes,
  snapshot: FloorSheetFsTimesLogSnapshot | null | undefined
): boolean {
  if (!snapshot) return false
  const pairs: Array<[FloorSheetFsTimeSlot, string | null]> = [
    [fsTimes.rootComplete, snapshot.rootCompleteAt],
    [fsTimes.hotPassStart, snapshot.hotPassStartAt],
    [fsTimes.weldCompleted, snapshot.weldCompletedAt]
  ]
  for (const [slot, snapIso] of pairs) {
    const filled = Boolean(slot.date.trim() || slot.time.trim())
    if (!filled && !snapIso) continue
    if (!snapIso) {
      if (filled) return true
      continue
    }
    if (slotKey(slot) !== slotKey(isoToSlot(snapIso))) return true
  }
  return false
}

/**
 * Apply derived times onto an existing fsTimes object.
 * When `overwrite` is false, only blank slots are filled.
 */
export function mergeDerivedFsTimes(
  existing: FloorSheetFsTimes,
  derived: FloorSheetFsTimes,
  overwrite: boolean
): FloorSheetFsTimes {
  const keys: Array<keyof FloorSheetFsTimes> = [
    'rootComplete',
    'hotPassStart',
    'weldCompleted'
  ]
  const next = { ...existing }
  for (const key of keys) {
    const d = derived[key]
    const hasDerived = Boolean(d.date.trim() || d.time.trim())
    if (!hasDerived) continue
    const e = existing[key]
    const hasExisting = Boolean(e.date.trim() || e.time.trim())
    if (overwrite || !hasExisting) next[key] = { ...d }
  }
  return next
}
